import { useState } from 'react';
import { toast } from 'sonner';
import { Send, Clock, AlertTriangle, X, Info, ImagePlus, Trash2, Check, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { FileRequestTrace } from '@/components/FileRequestTrace';
import { useFileRequestCategories, useFileRequestCopy, copyFor } from '@/lib/fileRequestCategories';

/**
 * Editor-facing file/info request — composer + "awaiting client" state.
 *
 * Lives directly under FootageLinks in the drawer's Files tab: the moment an
 * editor SEES the footage is the moment they notice a card is missing, so the
 * ask sits where the discovery happens rather than behind a menu.
 *
 * The note goes verbatim to the client, so the warning sits ABOVE the textarea,
 * not below it. Below is a disclaimer you read after you have already typed your
 * name; above is a constraint you read before you start. Nothing is stripped
 * automatically — per product decision we would rather see a real note that
 * leaks than pre-solve a problem we have not observed.
 *
 * TWO STEPS (operator, 2026-09-23, after a note reached a client in broken English):
 * the editor writes, presses "Clean up wording", and reads Claude's rewrite in an
 * editable field before "Send to client" appears (app/file_request_polish.py). The
 * server refuses a send that skipped the step. When Claude is unavailable the editor
 * is told so and sends their own words.
 *
 * The awaiting state is deliberately NOT a stop sign. Editors can usually keep
 * working with the footage they do have, and no date has moved, so it uses the
 * informational teal rather than a status red: "the ball is not in your court",
 * not "you are blocked".
 */

const MAX_IMAGES = 3;
const MAX_BYTES = 20 * 1024 * 1024;
const ALLOWED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const NOTE_MAX = 200;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

export function FileRequestPanel({ project, onRefresh }) {
  const openReq = (project.file_requests || []).find(r => r.status === 'open');
  const [composing, setComposing] = useState(false);
  const [category, setCategory] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);   // [{file_id, filename, url}]
  // The clean-up: {id, available, message} once the editor has pressed "Clean up wording",
  // and the words that will be sent (the rewrite, which the editor may edit).
  const [polish, setPolish] = useState(null);
  const [sendText, setSendText] = useState('');
  const [polishing, setPolishing] = useState(false);
  const categories = useFileRequestCategories();
  const copy = useFileRequestCopy();
  const [uploading, setUploading] = useState(false);
  const t = (key) => copyFor(copy, key, openReq?.category || '');

  // The drawer's "Chat with Admin (not the client)" bar renders its own toggle at
  // data-testid="chat-toggle-internal". Clicking it is the exit. If the bar is not
  // mounted for some reason the wording still tells them where to go, so this
  // degrades to a no-op rather than an error.
  const openAdminChat = () => {
    const t = document.querySelector('[data-testid="chat-toggle-internal"]');
    if (t) { t.click(); t.scrollIntoView({ block: 'nearest' }); }
  };

  const addImages = async (files) => {
    const room = MAX_IMAGES - images.length;
    if (room <= 0) { toast.error(`Up to ${MAX_IMAGES} screenshots`); return; }
    setUploading(true);
    try {
      for (const f of Array.from(files).slice(0, room)) {
        if (!ALLOWED.includes(f.type)) { toast.error(`${f.name}: images only`); continue; }
        if (f.size > MAX_BYTES) { toast.error(`${f.name}: over 20MB`); continue; }
        const { data: pre } = await api.post('/storage/presign-upload', {
          filename: f.name, scope: 'project_file_request', project_id: project.id,
          content_type: f.type, size_bytes: f.size,
        });
        if (pre.upload_url) {
          await fetch(pre.upload_url, { method: 'PUT', body: f, headers: { 'Content-Type': f.type } });
        }
        await api.post(`/storage/confirm-upload/${pre.file_id}`, {});
        setImages(prev => [...prev, {
          file_id: pre.file_id, filename: f.name, url: URL.createObjectURL(f),
        }]);
      }
    } catch (e) {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  /* FIX 2 — the editor closes or replaces a request the CLIENT HAS ANSWERED.
   *
   * Both controls only ever appear once a response exists, and the server enforces
   * the same rule independently (409 otherwise) — this is a safety property, not a
   * UI convenience. An unanswered request stays admin-only to withdraw, so an
   * editor can never make a question vanish from under a client who was about to
   * answer it.
   *
   * Before this, `cannot_provide` left the request open forever with no editor
   * control anywhere, and ONE-OPEN-PER-PROJECT meant the editor could not raise
   * anything else either — the most common outcome of the feature put admin back
   * in the critical path the feature exists to remove.
   */
  const closeRequest = async (action) => {
    setSaving(true);
    try {
      await api.put(`/projects/${project.id}/file-requests/${openReq.request_number}/close`,
                    { action });
      toast.success('Closed. Admin has been told it no longer needs attention');
      if (onRefresh) onRefresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' && (d?.message || d?.error)
        ? (d.message || d.error) : 'Could not close the request');
      if (onRefresh) onRefresh();
    } finally { setSaving(false); }
  };

  const cleanUp = async () => {
    if (!category) { toast.error('Pick what you need first'); return; }
    if (!note.trim()) { toast.error('Add a short note saying what is missing'); return; }
    setPolishing(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/file-requests/polish`, { category, note: note.trim() });
      setPolish({ id: data.polish_id, available: data.available, message: data.message || '' });
      setSendText(data.available ? data.polished : note.trim());
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not clean up the wording. Please try again in a minute.');
    } finally { setPolishing(false); }
  };

  const backToDraft = () => { setPolish(null); setSendText(''); };

  const submit = async () => {
    if (!category) { toast.error('Pick what you need first'); return; }
    if (!polish) { toast.error('Clean up the wording first, then send.'); return; }
    if (!sendText.trim()) { toast.error('Add a short note saying what is missing'); return; }
    setSaving(true);
    try {
      // `supersedes` makes replacing atomic. When the composer was opened by "Ask
      // for something else" there is still an open request, and naming it here is
      // what closes it — in the SAME write that creates this one. Nothing has
      // happened until this call succeeds, so an editor who opens the composer and
      // changes their mind leaves the original request open and untouched.
      await api.post(`/projects/${project.id}/file-requests`, {
        category, note: sendText.trim(), polish_id: polish.id, image_ids: images.map(i => i.file_id),
        ...(openReq ? { supersedes: openReq.request_number } : {}),
      });
      toast.success(openReq ? 'Replaced. The client has the new request'
                            : 'Request sent to the client');
      setComposing(false); setCategory(''); setNote(''); setImages([]); backToDraft();
      if (onRefresh) onRefresh();
    } catch (e) {
      // 409 means one is already open — surface what was already asked rather
      // than a generic failure (the server returns the existing request for
      // exactly this reason). A string detail is a sentence for the editor; the
      // clean-up one (stale, or already used) sends them back to clean up again.
      const d = e?.response?.data?.detail;
      if (typeof d === 'string') { toast.error(d); if (/Clean up/.test(d)) backToDraft(); }
      else toast.error(typeof d === 'object' && d?.error ? d.error : 'Could not send the request');
      if (onRefresh) onRefresh();
    } finally { setSaving(false); }
  };

  // ── Awaiting client ────────────────────────────────────────────────────────
  // `&& !composing` is what lets a REPLACEMENT be composed while the request it
  // replaces is still open. Before the atomic change the old request was already
  // closed by the time the composer opened, so this early return could be
  // unconditional; now the composer has to be reachable with `openReq` still set,
  // because the replacement names it and closes it on submit.
  if (openReq && !composing) {
    const answered = (openReq.responses || []).slice(-1)[0];
    const cannotProvide = answered?.verdict === 'cannot_provide';
    return (
      <div className="rounded-xl bg-ug-raised border border-oxidized-teal/40 p-5"
           data-testid="file-request-awaiting">
        <div className="flex items-start gap-2.5">
          <Clock className="w-4 h-4 text-oxidized-teal shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-oxidized-teal block"
                  data-testid="file-request-awaiting-title">
              {/* A STATE LABEL MUST FOLLOW THE STATE. This said "Awaiting client"
                  unconditionally, so it sat directly above the client's reply
                  telling the editor to keep waiting for it. Same bug as the
                  awaiting sentence below, found separately — see KNOWN-ISSUES #20
                  for the sweep that looked for the rest of the class.

                  The body line beneath is NOT duplicated here: the title says the
                  state changed, the body says what they said. */}
              {t(answered ? 'editor_panel.replied_title' : 'editor_panel.awaiting_title')}
            </span>
            {/* Says the ball is elsewhere WITHOUT implying the job has stopped —
                no date has moved and the editor can usually keep going.

                UNANSWERED ONLY. This sentence ends "we'll let you know when they
                reply", so it cannot outlive the reply. It used to render outside
                this conditional, which put "we'll let you know when they reply"
                directly above "The client can't provide this." — the editor was
                told to wait for something that had already arrived, on the same
                screen that showed it had.

                Four complete sentences keyed by category, not one frame with a
                noun dropped in. The frame is what produced "You asked for a
                question about the files". */}
            {!answered && (
              <p className="font-sans text-[11px] text-zinc-400 mt-1"
                 data-testid="file-request-awaiting-sentence">
                {copyFor(copy, 'editor_panel.awaiting_sentence', openReq.category)
                  .replace('{date}', fmtDate(openReq.created_at))}
              </p>
            )}
            <p className="font-sans text-[11px] text-zinc-300 mt-2 rounded-md bg-ug-surface border border-ug-border px-2.5 py-2"
               data-testid="file-request-awaiting-note">
              {openReq.note}
            </p>
            {answered && (
              <div className="mt-2.5" data-testid="file-request-client-reply">
                {/* NO files_uploaded BRANCH. It could not render: that verdict
                    resolves the request server-side, this panel only ever holds an
                    OPEN one, so the request is gone before any "files are uploaded"
                    line could be drawn. It sat here unreachable, drifting away from
                    the live email that says the same thing. See KNOWN-ISSUES #18 —
                    a live-updating panel would need it back. */}
                <span className="font-sans text-[9px] font-semibold uppercase tracking-wider block text-ug-status-red"
                      data-testid="file-request-cannot-provide-title">
                  {t('editor_panel.cannot_provide_title')}
                </span>
                {answered.note && (
                  <p className="font-sans text-[11px] text-zinc-300 mt-1">{answered.note}</p>
                )}
                {cannotProvide && (
                  <>
                    {/* SHARED with the editor's "can't provide" email by key.
                        These two drifted into "close this" and "close the request"
                        while each language held its own copy. */}
                    <p className="font-sans text-[10px] text-zinc-500 mt-1"
                       data-testid="file-request-cannot-provide-next">
                      {t('editor_panel.cannot_provide_next')}
                    </p>
                    {/* Equal weight, same row, same reasoning as the client's two
                        verdicts: neither of these is the "good" outcome. Closing
                        means the editor solved it another way; superseding means
                        they need something different. Styling one as primary would
                        push editors toward it. */}
                    <div className="flex items-center gap-2 mt-2.5"
                         data-testid="file-request-editor-actions">
                      {/* Opens the composer and NOTHING ELSE. It used to close the
                          request first, which committed half of a two-part action
                          and told admin it had been "replaced" before a replacement
                          existed. The close now happens on submit, atomically. */}
                      <button onClick={() => setComposing(true)}
                              disabled={saving}
                              data-testid="file-request-supersede"
                              className="tap-auto flex-1 flex items-center justify-center gap-1.5 rounded-md bg-ug-surface border border-ug-border px-3 py-2 font-sans text-[10px] font-semibold text-zinc-200 hover:border-ug-gold/50 transition-colors disabled:opacity-50">
                        <Send className="w-3 h-3" /> {t('editor_panel.action_supersede')}
                      </button>
                      <button onClick={() => closeRequest('resolve')}
                              disabled={saving}
                              data-testid="file-request-editor-resolve"
                              className="tap-auto flex-1 flex items-center justify-center gap-1.5 rounded-md bg-ug-surface border border-ug-border px-3 py-2 font-sans text-[10px] font-semibold text-zinc-200 hover:border-ug-gold/50 transition-colors disabled:opacity-50">
                        <Check className="w-3 h-3" /> {t('editor_panel.action_resolve')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Entry point ────────────────────────────────────────────────────────────
  if (!composing) {
    return (
      <>
      <div className="rounded-xl bg-ug-raised border border-ug-border p-5" data-testid="file-request-entry">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/60 block">
              Missing something?
            </span>
            <p className="font-sans text-[11px] text-zinc-400 mt-1">
              Ask the client directly. They&rsquo;re emailed straight away.
            </p>
          </div>
          <button onClick={() => setComposing(true)}
                  data-testid="file-request-open-composer"
                  className="tap-auto shrink-0 flex items-center gap-1.5 rounded-md bg-ug-surface border border-ug-border px-3 py-1.5 font-sans text-[10px] font-semibold text-zinc-200 hover:border-ug-gold/40 transition-colors">
            <Send className="w-3 h-3" /> Request files
          </button>
        </div>
      </div>
      {/* The editor's own history: the last closed request, so "did I already ask
          for this?" has an answer on the page where they'd re-ask it. */}
      <FileRequestTrace project={project} categories={categories} copy={copy} />
      </>
    );
  }

  // ── Composer ───────────────────────────────────────────────────────────────
  return (
    <div className="rounded-xl bg-ug-raised border border-ug-border p-5" data-testid="file-request-composer">
      <div className="flex items-center justify-between mb-3">
        <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/60">
          Request files or information
        </span>
        <button onClick={() => { setComposing(false); backToDraft(); }} data-testid="file-request-cancel"
                className="tap-auto text-zinc-500 hover:text-zinc-300 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* ROUTING GATE — first thing in the composer, readable BEFORE a category is
          picked, because picking one is already committing to the client path.
          TEAL not gold: this is informational ("are you in the right place?"),
          while the gold block below is a caution about what you type. Two gold
          blocks in one small composer trains the eye to skim both, and the gold
          one is the one that protects identity. */}
      <div className="flex items-start gap-2 rounded-md border border-oxidized-teal/40 bg-oxidized-teal/[0.06] px-2.5 py-2 mb-3"
           data-testid="file-request-routing-gate">
        <Info className="w-3.5 h-3.5 text-oxidized-teal shrink-0 mt-px" />
        <p className="font-sans text-[10px] text-zinc-300 leading-snug">
          <span className="font-semibold text-oxidized-teal">Only for footage you&rsquo;ve partly received.</span>{' '}
          A card that didn&rsquo;t come through, an audio file that isn&rsquo;t in the folder, a section you can&rsquo;t find.
          <br />
          <span className="font-semibold text-oxidized-teal">Can&rsquo;t find or open the files at all?</span>{' '}
          That&rsquo;s usually a broken link or permissions on our end, so{' '}
          <button type="button" onClick={openAdminChat} data-testid="file-request-admin-exit"
                  className="tap-auto underline underline-offset-2 text-oxidized-teal hover:text-oxidized-teal/80 font-semibold">
            message admin below
          </button>{' '}instead.
        </p>
      </div>

      <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-zinc-500 block mb-1.5">
        What do you need?
      </label>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {categories.map(c => (
          <button key={c.value} onClick={() => setCategory(c.value)}
                  data-testid={`file-request-category-${c.value}`}
                  className={`tap-auto rounded-md px-2.5 py-1.5 font-sans text-[10px] font-semibold border transition-colors ${
                    category === c.value
                      ? 'bg-ug-surface border-ug-gold/50 text-ug-gold'
                      : 'bg-transparent border-ug-border text-zinc-400 hover:border-ug-gold/30'}`}>
            {c.label}
          </button>
        ))}
      </div>

      {/* ABOVE the textarea on purpose — see the component docstring. An editor
          typing at 3am must register "this goes to the client" before the first
          keystroke, not after they have signed their name. */}
      <div className="flex items-start gap-2 rounded-md border border-ug-gold/40 bg-ug-gold/[0.06] px-2.5 py-2 mb-2"
           data-testid="file-request-note-warning">
        <AlertTriangle className="w-3.5 h-3.5 text-ug-gold shrink-0 mt-px" />
        <p className="font-sans text-[10px] text-zinc-300 leading-snug">
          <span className="font-semibold text-ug-gold">This note and any screenshots go straight to the client.</span>{' '}
          Say what&rsquo;s missing and where you looked. Don&rsquo;t include your name or anything that identifies you,
          and crop screenshots to just the folder, checking for names in file paths and title bars.
        </p>
      </div>

      {!polish ? (
        <textarea value={note} onChange={e => setNote(e.target.value.slice(0, NOTE_MAX))}
                  data-testid="file-request-note"
                  rows={3}
                  placeholder="The reception ceremony files from card 3 aren't in the Dropbox."
                  className="w-full rounded-md bg-ug-surface border border-ug-border px-2.5 py-2 font-sans text-[11px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-ug-gold/50 resize-none" />
      ) : (
        <div data-testid="file-request-polished">
          <p className="font-sans text-[10px] text-zinc-500 leading-snug mb-2" data-testid="file-request-your-draft">
            <span className="font-semibold">Your draft:</span> {note.trim()}
          </p>
          {polish.available ? (
            <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-oxidized-teal block mb-1.5">
              This is what the client will read. Change anything you like.
            </label>
          ) : (
            <p className="font-sans text-[10px] text-zinc-300 leading-snug mb-1.5" data-testid="file-request-polish-unavailable">
              {polish.message}
            </p>
          )}
          <textarea value={sendText} onChange={e => setSendText(e.target.value.slice(0, NOTE_MAX))}
                    data-testid="file-request-send-text"
                    rows={3}
                    className="w-full rounded-md bg-ug-surface border border-oxidized-teal/50 px-2.5 py-2 font-sans text-[11px] text-zinc-200 focus:outline-none focus:border-oxidized-teal resize-none" />
        </div>
      )}

      {/* PREVIEW, not a confirmation dialog: the image renders at the size the
          client will see it, inline, immediately after upload. Paths, OS
          usernames and full-screen grabs are unfixable server-side — the only
          control is the editor seeing the frame before it goes. No extra click;
          the send button stays where it was. */}
      {images.length > 0 && (
        <div className="mt-2.5 space-y-2" data-testid="file-request-image-previews">
          {images.map((img, i) => (
            <div key={img.file_id} className="relative rounded-md border border-ug-border bg-ug-surface p-1.5">
              <img src={img.url} alt={img.filename}
                   data-testid={`file-request-image-${i}`}
                   className="w-full rounded-sm block" />
              <button onClick={() => setImages(prev => prev.filter(x => x.file_id !== img.file_id))}
                      data-testid={`file-request-image-remove-${i}`}
                      title="Remove"
                      className="tap-auto absolute top-2.5 right-2.5 rounded-md bg-ug-deepest/80 border border-ug-border p-1 text-zinc-300 hover:text-ug-status-red transition-colors">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          <p className="font-sans text-[9px] text-zinc-500">
            This is what the client will see.
          </p>
        </div>
      )}

      {images.length < MAX_IMAGES && (
        <label data-testid="file-request-add-image"
               className="tap-auto mt-2.5 flex items-center justify-center gap-1.5 rounded-md border border-dashed border-ug-border px-3 py-2 font-sans text-[10px] font-semibold text-zinc-400 hover:border-ug-gold/40 hover:text-zinc-300 transition-colors cursor-pointer">
          <ImagePlus className="w-3.5 h-3.5" />
          {uploading ? 'Uploading…' : `Add a screenshot (${images.length}/${MAX_IMAGES})`}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple hidden
                 onChange={e => { addImages(e.target.files); e.target.value = ''; }} />
        </label>
      )}

      <div className="flex items-center justify-between gap-2 mt-2">
        <span className={`font-mono text-[9px] ${(polish ? sendText : note).length >= NOTE_MAX ? 'text-ug-status-red' : 'text-zinc-500'}`}
              data-testid="file-request-note-count">
          {(polish ? sendText : note).length}/{NOTE_MAX}
        </span>
        {!polish ? (
          <button onClick={cleanUp} disabled={polishing}
                  data-testid="file-request-clean-up"
                  className="tap-auto flex items-center gap-1.5 rounded-md bg-ug-surface border border-ug-gold/50 px-3 py-1.5 font-sans text-[10px] font-semibold text-ug-gold hover:bg-ug-gold/10 transition-colors disabled:opacity-50">
            <Sparkles className="w-3 h-3" /> {polishing ? 'Cleaning up…' : 'Clean up wording'}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={backToDraft} disabled={saving}
                    data-testid="file-request-back-to-draft"
                    className="tap-auto font-sans text-[10px] font-semibold text-zinc-400 hover:text-zinc-200 transition-colors disabled:opacity-50">
              Back to my draft
            </button>
            <button onClick={submit} disabled={saving}
                    data-testid="file-request-submit"
                    className="tap-auto flex items-center gap-1.5 rounded-md bg-ug-surface border border-ug-gold/50 px-3 py-1.5 font-sans text-[10px] font-semibold text-ug-gold hover:bg-ug-gold/10 transition-colors disabled:opacity-50">
              <Send className="w-3 h-3" /> {saving ? 'Sending…' : 'Send to client'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FileRequestPanel;
